import type { ActionKey, BotCommand, BotSlot, BotStatus, ModuleKey } from './types';

const moduleByAction: Partial<Record<ActionKey, ModuleKey>> = {
  collect: 'collect',
  train_troops: 'train_troops',
  auto_hunt: 'auto_hunt'
};

const busyStates = new Set(['collecting', 'training', 'hunting', 'auto_bot', 'running', 'stopping']);

export function hasPendingCommand(commands: BotCommand[]): boolean {
  return commands.some((command) => command.status === 'queued' || command.status === 'delivered');
}

export function hasPendingStop(commands: BotCommand[]): boolean {
  return commands.some(
    (command) => command.action === 'stop' && (command.status === 'queued' || command.status === 'delivered')
  );
}

export function isAgentBusy(status?: BotStatus | null): boolean {
  if (!status) return false;
  return status.running || busyStates.has(status.state);
}

export function actionAvailability(
  slot: BotSlot | null,
  status: BotStatus | null,
  commands: BotCommand[],
  action: ActionKey
): { allowed: boolean; reason?: string } {
  if (!slot) return { allowed: false, reason: 'Sin slot seleccionado' };
  if (!slot.linked) return { allowed: false, reason: 'Slot sin vincular' };

  if (action !== 'stop' && !['active', 'trial'].includes(slot.licenseStatus)) {
    return { allowed: false, reason: 'Licencia inactiva' };
  }

  const pending = hasPendingCommand(commands);
  const pendingStop = hasPendingStop(commands);
  if (pendingStop && action === 'stop') return { allowed: false, reason: 'Stop ya pendiente' };
  if (pending && action !== 'stop') return { allowed: false, reason: 'Hay un comando pendiente' };
  if (status?.screenBlocker?.kind && action !== 'stop') {
    return { allowed: false, reason: 'Cuenta bloqueada: reconecta primero' };
  }
  if (isAgentBusy(status) && action !== 'stop') return { allowed: false, reason: 'Bot ejecutando una accion' };

  if (action === 'auto_bot') {
    const modules = slot.enabledModules;
    if (!modules.collect && !modules.train_troops && !modules.auto_hunt && !modules.intelligence) {
      return { allowed: false, reason: 'Auto Bot sin modulos activos' };
    }
    return { allowed: true };
  }

  const moduleKey = moduleByAction[action];
  if (moduleKey && slot.enabledModules[moduleKey] === false) {
    return { allowed: false, reason: `Modulo desactivado: ${moduleKey}` };
  }

  return { allowed: true };
}
